import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from './ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

function VoiceChat({ userId }) {
  const { theme } = useTheme();
  const [isListening, setIsListening] = useState(false);
  const [status, setStatus] = useState('Tap waveform to start listening');
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [showOnboarding, setShowOnboarding] = useState(true);
  const [showDebug, setShowDebug] = useState(false);
  const [debugLogs, setDebugLogs] = useState([]);
  const [currentChunkDisplay, setCurrentChunkDisplay] = useState('');

  const recognitionRef = useRef(null);
  const debugLogsRef = useRef(null);
  const currentChunkRef = useRef('');
  const timeoutRef = useRef(null);
  const silenceTimeoutRef = useRef(null);
  const postTriggerTextRef = useRef('');
  const isRecordingPostTriggerRef = useRef(false);
  const currentAudioRef = useRef(null);
  const triggerDetectedRef = useRef(false);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const microphoneRef = useRef(null);
  const animationFrameRef = useRef(null);

  useEffect(() => {
    const hasSeenOnboarding = localStorage.getItem('syntari_onboarding_seen');
    if (hasSeenOnboarding) {
      setShowOnboarding(false);
    }
  }, []);

  useEffect(() => {
    console.log('Initializing speech recognition...');
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      console.error('Speech recognition not supported');
      setStatus('Speech recognition not supported');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onresult = (event) => {
      let finalTranscript = '';
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript + ' ';
        } else {
          interimTranscript += transcript;
        }
      }

      if (interimTranscript) {
        addDebugLog('voice', 'Interim transcription', interimTranscript);
      }

      if (finalTranscript) {
        addDebugLog('voice', 'Final transcription', finalTranscript);
        if (isPlayingAudio) {
          window.speechSynthesis.cancel();
          if (currentAudioRef.current) {
            currentAudioRef.current.pause();
            currentAudioRef.current = null;
          }
          setIsPlayingAudio(false);
          setStatus('Listening...');
        }

        const lowerTranscript = finalTranscript.toLowerCase();
        const hasTrigger = lowerTranscript.includes('centauri') || lowerTranscript.includes('syntari');

        if (hasTrigger && !triggerDetectedRef.current) {
          addDebugLog('trigger', 'Trigger word detected!', finalTranscript);
          handleTriggerWordDetection(finalTranscript);
        } else if (triggerDetectedRef.current || isRecordingPostTriggerRef.current) {
          postTriggerTextRef.current += finalTranscript;
          addDebugLog('trigger', 'Post-trigger text', postTriggerTextRef.current);
          resetSilenceTimer();
        } else {
          currentChunkRef.current += finalTranscript;
          setCurrentChunkDisplay(currentChunkRef.current);
          addDebugLog('buffer', 'Adding to current chunk', currentChunkRef.current);

          if (currentChunkRef.current.split(' ').length > 50) {
            addDebugLog('buffer', 'Chunk word limit reached (50 words)', null);
            sendChunk(false);
          }
        }
      }
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setStatus(`Error: ${event.error}`);
    };

    recognition.onstart = () => {
      setStatus('Listening...');

      timeoutRef.current = setTimeout(() => {
        if (currentChunkRef.current.trim()) {
          sendChunk(false);
        }

        if (isRecordingPostTriggerRef.current && postTriggerTextRef.current.trim()) {
          sendTriggerMessage();
        }

        triggerDetectedRef.current = false;

        if (recognitionRef.current) {
          recognitionRef.current.stop();
        }
        setIsListening(false);
        setStatus('Tap waveform to start listening');
      }, 300000);
    };

    recognition.onend = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      if (isListening) {
        setTimeout(() => {
          if (recognitionRef.current && isListening) {
            recognitionRef.current.start();
          }
        }, 100);
      }
    };

    recognitionRef.current = recognition;

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
        silenceTimeoutRef.current = null;
      }
      if (recognitionRef.current) {
        recognitionRef.current.stop();
        recognitionRef.current = null;
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  const setupAudioAnalyzer = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      microphoneRef.current = audioContextRef.current.createMediaStreamSource(stream);

      analyserRef.current.fftSize = 256;
      analyserRef.current.smoothingTimeConstant = 0.8;
      microphoneRef.current.connect(analyserRef.current);

      updateAudioLevel();
    } catch (error) {
      console.error('Error accessing microphone:', error);
    }
  };

  const updateAudioLevel = () => {
    if (!analyserRef.current) return;

    const bufferLength = analyserRef.current.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    analyserRef.current.getByteFrequencyData(dataArray);

    const average = dataArray.reduce((acc, val) => acc + val, 0) / bufferLength;
    const normalized = Math.min(average / 128, 1);

    setAudioLevel(normalized);

    if (isListening) {
      animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
    }
  };

  const addDebugLog = (type, message, data = null) => {
    const timestamp = new Date().toLocaleTimeString();
    setDebugLogs(prev => [{ timestamp, type, message, data }, ...prev]);
  };

  useEffect(() => {
    if (debugLogsRef.current && showDebug) {
      debugLogsRef.current.scrollTop = 0;
    }
  }, [debugLogs, showDebug]);

  const sendChunk = async (isTrigger) => {
    if (!currentChunkRef.current.trim()) return;

    const chunk = currentChunkRef.current.trim();
    addDebugLog('chunk', `Sending ${isTrigger ? 'TRIGGER' : 'regular'} chunk`, chunk);
    await sendChunkToBackend(chunk, isTrigger);
    currentChunkRef.current = '';
    setCurrentChunkDisplay('');
  };

  const sendChunkToBackend = async (text, isTrigger) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/send-chunk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          text: text,
          is_trigger: isTrigger,
        }),
      });

      if (!response.ok) throw new Error('Failed to send chunk');

      if (isTrigger) {
        setIsWaitingForResponse(true);
        setStatus('Processing...');

        const processResponse = await fetch(`${BACKEND_URL}/api/process`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: userId }),
        });

        if (!processResponse.ok) throw new Error('Failed to process');

        const contentType = processResponse.headers.get('content-type');
        setIsWaitingForResponse(false);

        // Check if response is audio or JSON (fallback)
        if (contentType && contentType.includes('audio')) {
          const audioBlob = await processResponse.blob();
          playAudio(audioBlob);
        } else {
          const data = await processResponse.json();
          speakText(data.response_text);
        }
      }
    } catch (error) {
      console.error('Error sending chunk:', error);
      setStatus('Error sending message');
      setIsWaitingForResponse(false);
    }
  };

  const handleTriggerWordDetection = (triggerTranscript) => {
    if (currentChunkRef.current.trim()) {
      sendChunk(false);
    }

    const lowerTranscript = triggerTranscript.toLowerCase();
    const triggerIndex = lowerTranscript.indexOf('centauri') !== -1 ?
      lowerTranscript.indexOf('centauri') : lowerTranscript.indexOf('syntari');

    const beforeTrigger = triggerTranscript.substring(0, triggerIndex).trim();
    const afterTrigger = triggerTranscript.substring(triggerIndex).trim();

    const triggerWord = lowerTranscript.includes('centauri') ? 'centauri' : 'syntari';
    const triggerWordLength = triggerWord.length;
    const postTriggerText = afterTrigger.substring(triggerWordLength).trim();

    if (beforeTrigger) {
      currentChunkRef.current = beforeTrigger + ' ';
      sendChunk(false);
    }

    triggerDetectedRef.current = true;
    isRecordingPostTriggerRef.current = true;
    postTriggerTextRef.current = postTriggerText;
    setStatus('Continue speaking...');

    resetSilenceTimer();
  };

  const resetSilenceTimer = () => {
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
    }

    silenceTimeoutRef.current = setTimeout(() => {
      if (isRecordingPostTriggerRef.current && postTriggerTextRef.current.trim()) {
        sendTriggerMessage();
      }
    }, 3000);
  };

  const sendTriggerMessage = () => {
    const triggerMessage = postTriggerTextRef.current.trim();
    if (triggerMessage) {
      sendChunkToBackend(triggerMessage, true);
    }

    triggerDetectedRef.current = false;
    isRecordingPostTriggerRef.current = false;
    postTriggerTextRef.current = '';
    setStatus('Message sent!');

    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
    }
  };

  const playAudio = (blob) => {
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }

    const audioUrl = URL.createObjectURL(blob);
    const audio = new Audio(audioUrl);
    currentAudioRef.current = audio;
    setIsPlayingAudio(true);
    setStatus('Playing response...');

    audio.play();

    audio.onended = () => {
      URL.revokeObjectURL(audioUrl);
      currentAudioRef.current = null;
      setIsPlayingAudio(false);
      setStatus('Listening...');
    };

    audio.onerror = () => {
      console.error('Audio playback error');
      URL.revokeObjectURL(audioUrl);
      currentAudioRef.current = null;
      setIsPlayingAudio(false);
      setStatus('Audio error');
    };
  };

  const speakText = (text) => {
    // Cancel any ongoing speech
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'en-US';
    utterance.rate = 1.0;
    utterance.pitch = 1.0;

    setIsPlayingAudio(true);
    setStatus('Playing response...');

    utterance.onend = () => {
      setIsPlayingAudio(false);
      setStatus('Listening...');
    };

    utterance.onerror = (error) => {
      console.error('Speech synthesis error:', error);
      setIsPlayingAudio(false);
      setStatus('Speech error');
    };

    window.speechSynthesis.speak(utterance);
  };

  const toggleListening = async () => {
    if (showOnboarding) {
      setShowOnboarding(false);
      localStorage.setItem('syntari_onboarding_seen', 'true');
    }

    if (isListening) {
      if (currentChunkRef.current.trim()) {
        sendChunk(false);
      }

      if (isRecordingPostTriggerRef.current && postTriggerTextRef.current.trim()) {
        sendTriggerMessage();
      }

      triggerDetectedRef.current = false;

      if (isPlayingAudio) {
        window.speechSynthesis.cancel();
        if (currentAudioRef.current) {
          currentAudioRef.current.pause();
          currentAudioRef.current = null;
        }
        setIsPlayingAudio(false);
      }

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
        silenceTimeoutRef.current = null;
      }
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      setIsListening(false);
      setIsWaitingForResponse(false);
      setStatus('Tap waveform to start listening');
      setAudioLevel(0);
    } else {
      currentChunkRef.current = '';
      postTriggerTextRef.current = '';
      isRecordingPostTriggerRef.current = false;
      triggerDetectedRef.current = false;

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
        silenceTimeoutRef.current = null;
      }

      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }

      await setupAudioAnalyzer();

      setIsListening(true);
      setStatus('Starting...');

      setTimeout(() => {
        if (recognitionRef.current) {
          recognitionRef.current.start();
        }
      }, 100);
    }
  };

  const getBarHeight = (index) => {
    if (!isListening) return 4;

    const audioInfluence = audioLevel * 40;
    const baseHeight = 8;
    const variation = Math.sin(index * 0.5) * 5;

    return baseHeight + audioInfluence + variation;
  };

  const styles = getStyles(theme);

  return (
    <div style={styles.container}>
      {showOnboarding && (
        <div style={styles.onboardingCard}>
          <h2 style={styles.onboardingTitle}>Welcome!</h2>
          <div style={styles.onboardingContent}>
            <p style={styles.onboardingText}>
              <strong>Click the waveform below to start speaking.</strong>
            </p>
            <p style={styles.onboardingText}>
              Syntari only activates when you use the keyword <strong>"Syntari"</strong> or <strong>"Centauri"</strong> every time you need something.
            </p>
            <p style={styles.onboardingText}>
              Use the menu in the top right for <strong>Settings</strong> and <strong>Search</strong> to explore your thoughts.
            </p>
          </div>
          <button
            onClick={() => {
              setShowOnboarding(false);
              localStorage.setItem('syntari_onboarding_seen', 'true');
            }}
            style={styles.onboardingButton}
          >
            Got it
          </button>
        </div>
      )}

      <div
        style={{
          ...styles.card,
          cursor: isWaitingForResponse ? 'not-allowed' : 'pointer',
          opacity: isWaitingForResponse ? 0.7 : 1,
        }}
        onClick={!isWaitingForResponse ? toggleListening : undefined}
      >
        <h3 style={styles.cardTitle}>
          {isListening ? 'Listening...' : 'Tap to start'}
        </h3>
        <div style={styles.waveform}>
          {[...Array(7)].map((_, i) => (
            <div
              key={i}
              style={{
                ...styles.waveBar,
                height: `${getBarHeight(i)}px`,
              }}
            />
          ))}
        </div>
        <p style={styles.status}>{status}</p>
      </div>

      {isWaitingForResponse && (
        <div style={styles.processingIndicator}>
          <div style={styles.spinner}></div>
          <p style={styles.processingText}>Processing your request...</p>
        </div>
      )}

      <button
        onClick={() => {
          setShowDebug(!showDebug);
          if (!showDebug) {
            setDebugLogs([]);
          }
        }}
        style={styles.debugButton}
      >
        {showDebug ? 'Hide Debug' : 'Show Debug'}
      </button>

      {showDebug && (
        <div style={styles.debugPanel}>
          <div style={styles.debugHeader}>
            <h4 style={styles.debugTitle}>Debug Console</h4>
            <button
              onClick={() => setDebugLogs([])}
              style={styles.clearButton}
            >
              Clear
            </button>
          </div>
          <div style={styles.debugLogs} ref={debugLogsRef}>
            {debugLogs.length === 0 ? (
              <p style={styles.debugEmpty}>No logs yet. Start speaking to see debug information.</p>
            ) : (
              debugLogs.map((log, index) => (
                <div key={index} style={styles.debugLogItem}>
                  <div style={styles.debugLogHeader}>
                    <span style={{
                      ...styles.debugLogType,
                      backgroundColor:
                        log.type === 'trigger' ? 'rgba(255, 68, 68, 0.2)' :
                        log.type === 'chunk' ? 'rgba(0, 212, 106, 0.2)' :
                        log.type === 'voice' ? 'rgba(79, 127, 255, 0.2)' :
                        'rgba(128, 128, 128, 0.2)',
                      color:
                        log.type === 'trigger' ? theme.error :
                        log.type === 'chunk' ? theme.primary :
                        log.type === 'voice' ? '#4F7FFF' :
                        theme.textSecondary
                    }}>
                      {log.type.toUpperCase()}
                    </span>
                    <span style={styles.debugLogTime}>{log.timestamp}</span>
                  </div>
                  <p style={styles.debugLogMessage}>{log.message}</p>
                  {log.data && (
                    <pre style={styles.debugLogData}>{log.data}</pre>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const getStyles = (theme) => ({
  container: {
    width: '100%',
    maxWidth: '600px',
    margin: '0 auto',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    minHeight: '100vh',
  },
  onboardingCard: {
    background: theme.surface,
    borderRadius: '20px',
    padding: '24px',
    border: `2px solid ${theme.primary}`,
    boxShadow: `0 8px 32px rgba(0, 212, 106, 0.2)`,
    animation: 'slideIn 0.5s ease',
  },
  onboardingTitle: {
    fontSize: '20px',
    fontWeight: '700',
    color: theme.text,
    marginBottom: '16px',
    textAlign: 'center',
  },
  onboardingContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginBottom: '20px',
  },
  onboardingText: {
    fontSize: '14px',
    lineHeight: '1.6',
    color: theme.textSecondary,
    margin: 0,
  },
  onboardingButton: {
    width: '100%',
    padding: '12px',
    fontSize: '15px',
    fontWeight: '600',
    color: '#FFFFFF',
    background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.primaryDark} 100%)`,
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'transform 0.2s, box-shadow 0.2s',
    boxShadow: `0 4px 16px rgba(0, 212, 106, 0.3)`,
  },
  card: {
    background: theme.surface,
    borderRadius: '20px',
    padding: '32px 24px',
    border: `1px solid ${theme.border}`,
    boxShadow: theme.background === '#FFFFFF' ? '0 4px 20px rgba(0, 0, 0, 0.08)' : '0 8px 32px rgba(0, 0, 0, 0.4)',
    transition: 'transform 0.2s, box-shadow 0.2s',
    cursor: 'pointer',
    userSelect: 'none',
  },
  cardTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: theme.text,
    marginBottom: '20px',
    textAlign: 'center',
  },
  waveform: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '7px',
    height: '70px',
    marginBottom: '16px',
  },
  waveBar: {
    width: '5px',
    background: `linear-gradient(180deg, ${theme.primary} 0%, ${theme.primaryDark} 100%)`,
    borderRadius: '3px',
    transition: 'height 0.1s ease-out',
    boxShadow: `0 0 10px rgba(0, 212, 106, 0.3)`,
  },
  status: {
    textAlign: 'center',
    color: theme.textTertiary,
    fontSize: '13px',
    margin: 0,
  },
  processingIndicator: {
    background: theme.surface,
    borderRadius: '14px',
    padding: '16px',
    border: `1px solid ${theme.border}`,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    justifyContent: 'center',
  },
  spinner: {
    width: '20px',
    height: '20px',
    border: `3px solid ${theme.border}`,
    borderTop: `3px solid ${theme.primary}`,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  processingText: {
    color: theme.primary,
    fontSize: '13px',
    fontWeight: '600',
    margin: 0,
  },
  debugButton: {
    padding: '12px 20px',
    fontSize: '14px',
    fontWeight: '700',
    color: '#FFFFFF',
    background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.primaryDark} 100%)`,
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    alignSelf: 'center',
    boxShadow: `0 4px 16px rgba(0, 212, 106, 0.3)`,
    marginTop: '8px',
  },
  debugPanel: {
    background: theme.surface,
    borderRadius: '14px',
    padding: '16px',
    border: `1px solid ${theme.border}`,
    boxShadow: theme.background === '#FFFFFF' ? '0 4px 20px rgba(0, 0, 0, 0.08)' : '0 8px 32px rgba(0, 0, 0, 0.4)',
  },
  debugHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
    paddingBottom: '12px',
    borderBottom: `1px solid ${theme.border}`,
  },
  debugTitle: {
    fontSize: '14px',
    fontWeight: '700',
    color: theme.text,
    margin: 0,
  },
  clearButton: {
    padding: '6px 12px',
    fontSize: '12px',
    fontWeight: '600',
    color: theme.error,
    background: 'transparent',
    border: `1px solid ${theme.error}`,
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  debugLogs: {
    maxHeight: '400px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  debugEmpty: {
    textAlign: 'center',
    color: theme.textSecondary,
    fontSize: '13px',
    padding: '20px',
    margin: 0,
  },
  debugLogItem: {
    background: theme.background,
    borderRadius: '8px',
    padding: '10px',
    border: `1px solid ${theme.border}`,
  },
  debugLogHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '6px',
  },
  debugLogType: {
    fontSize: '10px',
    fontWeight: '700',
    padding: '3px 8px',
    borderRadius: '4px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  debugLogTime: {
    fontSize: '11px',
    color: theme.textMuted,
    fontFamily: 'monospace',
  },
  debugLogMessage: {
    fontSize: '12px',
    color: theme.text,
    margin: '0 0 6px 0',
    fontWeight: '500',
  },
  debugLogData: {
    fontSize: '11px',
    color: theme.textSecondary,
    background: theme.surface,
    padding: '8px',
    borderRadius: '6px',
    margin: 0,
    fontFamily: 'monospace',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    border: `1px solid ${theme.border}`,
  },
});

export default VoiceChat;
